<template>
  <div class="introjs-wrap">
    <div class="quick-start" id="quickStart">
      <div class="quick-start-title">
        <h3>{{ t('Quick start') }}</h3>
        <button class="close" @click="quickStart">
          <ui-icon icon="fa fa-times"></ui-icon>
        </button>
      </div>
      <div class="quick-start-description">
        {{ t('Follow this guide to create your first campaign. Close Quick Start to go through the settings on your own. To access the guide again click ‘Support’ tab')}}.
      </div>
      <div class="quick-start-steps">
        <div class="quick-start-step">
          <span v-if="trafficSourceCount === 0" class="number">1</span>
          <span v-if="trafficSourceCount > 0" class="done"><ui-icon icon="fa fa-check"></ui-icon></span>
          <h4>{{ t('Traffic Source Settings') }}</h4>
          <p>
            {{ t('Set up the platform which generates traffic for your campaign. Make sure you have an active account with this traffic source')}}.
          </p>

          <button
            class="btn btn-success"
            :class="{ 'btn-done': trafficSourceCount > 0 }"
            @click="tutorial.start('traffic-sources')"
          >
            {{ t('Start') }}
          </button>
        </div>
        <div class="quick-start-step quick-start-step-addional">
          <hr class="line" />
          <h4>{{ t('Affiliate Network Settings (optional)') }}</h4>
          <p>{{ t('To pass on the conversions data to the tracker and enrich reports it is recommended to set up affiliate network.') }}</p>
          <button
            class="btn btn-success"
            :class="{ 'btn-done': affiliateNetworkCount > 0 }"
            @click="tutorial.start('affiliate-networks')"
          >
            {{ t('Start') }}
          </button>
        </div>
        <div class="quick-start-step">
          <span v-if="offerCount === 0" class="number">2</span>
          <span v-if="offerCount > 0" class="done"><ui-icon icon="fa fa-check"></ui-icon></span>
          <h4>{{ t('Offer Settings') }}</h4>
          <p>{{ t('An offer is any link you are promoting, which is the endpoint for traffic in campaign.') }}</p>
          <button
            class="btn btn-success"
            :class="{ 'btn-done': offerCount > 0 }"
            @click="tutorial.start('offers')"
          >
            {{ t('Start') }}
          </button>
        </div>
        <div class="quick-start-step quick-start-step-addional">
          <hr class="line" />
          <h4>{{ t('Landing Page Settings (optional)') }}</h4>
          <p>{{ t('A landing is an externally hosted page required to increase interest to the offer. Should contain BeMob Click URL.') }}</p>
          <button
            class="btn btn-success"
            @click="tutorial.start('landings')"
            :class="{ 'btn-done': landingCount > 0 }"
          >
            {{ t('Start') }}
          </button>
        </div>
        <div class="quick-start-step">
          <span v-if="campaignCount === 0" class="number">3</span>
          <span v-if="campaignCount > 0" class="done"><ui-icon icon="fa fa-check"></ui-icon></span>
          <h4>{{ t('Campaign Settings') }}</h4>
          <p>{{ t('All the elements configured on the previous steps should be gathered in a campaign. After the settings are saved, BeMob generates the unique campaign link.') }}</p>
          <button
            class="btn btn-success"
            :class="{ 'btn-done': campaignCount > 0 }"
            @click="tutorial.start('campaigns')"
          >
            {{ t('Start') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuickstart } from '@/components/tutorial/useQuickstart';

const {
  t,
  tutorial,
  affiliateNetworkCount,
  trafficSourceCount,
  campaignCount,
  offerCount,
  landingCount,
  quickStart,
} = useQuickstart();
</script>

<style lang="scss">
  [data-theme='bemob'] .introjs-wrap {

    z-index: 20;
    background-color: var(--color-graphite);
    width: 380px;
    float: left;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    // height: 100%;
    color: var(--color-white);
    overflow-x: hidden;
    overflow-y: auto;

    &.quick-start-none {
      display: none;
    }

    p {
      line-height: 23px;
    }

    .quick-start .quick-start-title {
      height: 80px;
      line-height: 80px;
      padding: 20px;
      border-bottom: 1px solid var(--color-graphite);

      h3 {
        font-weight: 500;
        font-size: 18px;
        margin: 0;
        padding: 0;
        vertical-align: middle;
        line-height: 40px;
        text-transform: uppercase;
        background: url("data:image/png;base64,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") no-repeat;
        padding-left: 40px;
      }

      .close {
        margin-top: -32px !important;
        margin-bottom: 0 !important;
        color: var(--color-white);
      }
    }

    button.close {
      padding: 0;
      cursor: pointer;
      background: transparent;
      border: 0;
      -webkit-appearance: none;
    }

    .close {
      float: right;
      font-size: 21px;
      font-weight: 700;
      line-height: 1;
      color: var(--color-black);
      text-shadow: 0 1px 0 var(--color-white);
      filter: alpha(opacity=20);
      filter: opacity(20);
      opacity: .2;
    }

    .quick-start .quick-start-description {
      padding: 20px;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
    }

    .quick-start .quick-start-steps {
      position: relative;

      .quick-start-step {
        border-left: 1px solid var(--color-graphite);
        margin: 0 0 0 35px;
        padding: 0 20px 0 30px;
        width: 345px;
        position: relative;
      }
    }

    .number {
      font-size: 14px;
      position: absolute;
      left: -16px;
      background: var(--color-graphite);
      color: var(--color-white);
      width: 30px;
      height: 30px;
      line-height: 30px;
      vertical-align: middle;
      text-align: center;
      border-radius: 15px;
    }

    .done > i {
      font-size: 14px;
      position: absolute;
      left: -16px;
      width: 30px;
      height: 30px;
      line-height: 30px;
      vertical-align: middle;
      text-align: center;
      border-radius: 15px;
      background: var(--color-green) !important;
      color: var(--color-white) !important;
    }

    .btn-done {
      background-color: var(--color-graphite) !important;
    }

    .quick-start .quick-start-steps .quick-start-step h4 {
      margin: 0 0 15px;
      padding: 8px 0 0;
      font-weight: 500;
      font-size: 14px;
      text-transform: uppercase;
    }

    .quick-start .quick-start-steps .quick-start-step p {
      margin: 0 0 15px;
      font-size: 12px;
      color: var(--color-gray10);
    }

    //btn
    .btn:not(.btn-sm):not(.btn-hg):not(.btn-xs):not(.btn-lg):not(.bootstrap-touchspin-up):not(.bootstrap-touchspin-down) {
      height: 32px;
    }

    .quick-start .quick-start-steps .quick-start-step button {
      margin: 0 0 25px;
    }

    .btn-success {
      background-color: var(--color-green);
      color: var(--color-white);

      &:hover {
        background-color: var(--color-green);
        border-color: var(--color-green);
        color: var(--color-white);
      }
    }

    .btn {
      border: none;
      font-size: 1em;
      font-weight: 400;
      padding: .375em .625em;
      transition: border .25s linear, color .25s linear,
      background-color .25s linear;
      vertical-align: middle;
      border-radius: 4px;
    }

    .btn {
      display: inline-block;
      margin-bottom: 0;
      text-align: center;
      touch-action: manipulation;
      cursor: pointer;
      background-image: none;
      white-space: nowrap;
      line-height: 1.42857143;
      user-select: none;
    }

    .quick-start .quick-start-steps .quick-start-step-addional {
      padding-left: 50px;
    }

    .quick-start .quick-start-steps .quick-start-step hr.line {
      position: absolute;
      left: 0;
      top: 5px;
      width: 30px;
      height: 2px;
      margin: 0;
      border-top: 2px solid var(--color-graphite);
    }

    .quick-start .quick-start-steps .quick-start-step:last-child {
      border: 0;
    }

    .quick-start .quick-start-steps .quick-start-step-addional h4 {
      padding: 0;
      font-size: 12px;
    }
  }


  // [v-intro-step='1'] .v-btn{
  //   z-index: 10000000 !important;
  // }
</style>